import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { Sort } from '@angular/material/sort'
import { DateService } from '@lemon/core/service/date.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import * as xlsx from 'xlsx'
import { LlmKirjausehdotusAjoTulosRow, LlmKirjausehdotusRow, LlmTestisettiKokoelma, LlmTestitapausKategoria } from '../../../_jaettu-lemonator/model/llm-kirjausehdotus-testaus'

interface Malli {
  tarjoaja: 'claude' | 'openai'
  malli: string
}

@Component({
  selector: 'app-kategoria-ajot',
  templateUrl: './kategoria-ajot.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KategoriaAjotComponent implements OnChanges {
  @Input() kategoria!: LlmTestitapausKategoria
  @Input() testiajot: LlmKirjausehdotusAjoTulosRow[] = []
  @Input() testiajotLataa = false
  @Input() setit: LlmTestisettiKokoelma[] = []
  @Input() testiajoKaynnissa = false
  @Input() tuloksetTestiajoAvain: string | null = null
  @Input() tulokset: LlmKirjausehdotusRow[] = []
  @Input() tuloksetLataa = false
  @Input() naytaPrompttiAvaimet: ReadonlySet<string> = new Set()
  @Input() kasiteltyLkm: ReadonlyMap<string, number> = new Map()

  @Input() mallit: Malli[] = []
  @Input() valittuMalli!: Malli
  @Input() valittuAjoSetti: LlmTestisettiKokoelma | null = null

  @Output() valittuMalliChange = new EventEmitter<Malli>()
  @Output() valittuAjoSettiChange = new EventEmitter<LlmTestisettiKokoelma | null>()
  @Output() aloitaTestiAjo = new EventEmitter<void>()
  @Output() testiajoPaneelAuki = new EventEmitter<LlmKirjausehdotusAjoTulosRow>()
  @Output() testiajoPaneelKiinni = new EventEmitter<string>()
  @Output() togglePrompt = new EventEmitter<string>()

  tuloksetSorted: LlmKirjausehdotusRow[] = []
  private _currentSort: Sort | null = null

  readonly tulosColumns = [
    'expand',
    'asiakas',
    'kirjausnumero',
    'saajaLisatieto',
    'kategoria',
    'todellisetTilit',
    'ehdotetutTilit',
    'todellinenAlv',
    'ehdotetutAlv',
    'osumat',
    'alvOsumat',
    'kaikkiOikein',
    'confidence',
    'virhe',
  ]

  avattuTulos: LlmKirjausehdotusRow | null = null

  ajoSettiKentta = form(signal<LlmTestisettiKokoelma | null>(null))
  malliKentta = form(signal<Malli | null>(null))

  constructor(private _dateService: DateService) {
    arvonMuutokset(this.ajoSettiKentta).subscribe(setti => this.valittuAjoSettiChange.emit(setti))
    arvonMuutokset(this.malliKentta).subscribe(malli => this.valittuMalliChange.emit(malli))
  }

  kaikkiOikein(t: LlmKirjausehdotusRow): boolean {
    return (
      t.todellisetTilit.length > 0 &&
      t.osumatLkm === t.todellisetTilit.length &&
      t.suuntaOsumatLkm === t.todellisetTilit.length &&
      t.summaOsumatLkm === t.todellisetTilit.length &&
      t.alvOsumatLkm === t.todellisetTilit.length
    )
  }

  toggleTulosRivi(t: LlmKirjausehdotusRow) {
    this.avattuTulos = this.avattuTulos === t ? null : t
    this.tuloksetSorted = [...this.tuloksetSorted]
  }

  onkoTulosRiviAuki = (_index: number, rivi: LlmKirjausehdotusRow): boolean => this.avattuTulos === rivi

  ngOnChanges(changes: SimpleChanges) {
    if (changes['valittuAjoSetti']) {
      asetaArvo(this.ajoSettiKentta, this.valittuAjoSetti ?? null, { ilmoita: false })
    }
    if (changes['valittuMalli']) {
      asetaArvo(this.malliKentta, this.valittuMalli ?? null, { ilmoita: false })
    }
    if (changes['tulokset']) {
      this._jarjestaTulokset()
    }
  }

  vertaileMallit(a: { malli: string }, b: { malli: string }): boolean {
    return a?.malli === b?.malli
  }

  vertaileSetit(a: LlmTestisettiKokoelma | null, b: LlmTestisettiKokoelma | null): boolean {
    return a?.avain === b?.avain
  }

  taydellisetProsentti(testiajo: LlmKirjausehdotusAjoTulosRow): number | null {
    if (!testiajo.kirjauksia || testiajo.taydellisetOsumat === null) {
      return null
    }
    return (testiajo.taydellisetOsumat / testiajo.kirjauksia) * 100
  }

  onTulosSortMuuttui(sort: Sort) {
    this._currentSort = sort
    this._jarjestaTulokset()
  }

  private _jarjestaTulokset() {
    const sort = this._currentSort
    if (!sort || !sort.active || sort.direction === '') {
      this.tuloksetSorted = [...this.tulokset]
      return
    }
    const isAsc = sort.direction === 'asc'
    this.tuloksetSorted = [...this.tulokset].sort((a, b) => {
      switch (sort.active) {
        case 'asiakas':
          return this._vertaa(a.asiakasNimi, b.asiakasNimi, isAsc)
        case 'kirjausnumero':
          return this._vertaa(a.kirjausnumero, b.kirjausnumero, isAsc)
        case 'kategoria':
          return this._vertaa(a.kategoria ?? '', b.kategoria ?? '', isAsc)
        case 'kaikkiOikein':
          return this._vertaa(this.kaikkiOikein(a) ? 1 : 0, this.kaikkiOikein(b) ? 1 : 0, isAsc)
        case 'confidence':
          return this._vertaa(a.confidenceLevel ?? -1, b.confidenceLevel ?? -1, isAsc)
        default:
          return 0
      }
    })
  }

  private _vertaa(a: string | number, b: string | number, isAsc: boolean): number {
    if (a === b) {
      return 0
    }
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1)
  }

  lataaTuloksetExcelina() {
    if (!this.tuloksetSorted.length) {
      return
    }
    const ajo = this.testiajot.find(t => t.avain === this.tuloksetTestiajoAvain)
    const rivit = this.tuloksetSorted.map(t => ({
      Asiakas: t.asiakasNimi,
      Kirjausnumero: t.kirjausnumero,
      Pvm: t.kirjausPvm,
      Summa: t.kirjauksenSumma,
      'Maksun saaja': t.maksunSaaja ?? '',
      Lisätieto: t.asiakkaanLisatieto ?? '',
      Kategoria: t.kategoria ?? '',
      'Todelliset tilit': t.todellisetTilit.join(', '),
      'Ehdotetut tilit': t.ehdotetutTilit.join(', '),
      'Todelliset debet': t.todellisetDebet.join(', '),
      'Ehdotetut debet': t.ehdotetutDebet.join(', '),
      'Todelliset kredit': t.todellisetKredit.join(', '),
      'Ehdotetut kredit': t.ehdotetutKredit.join(', '),
      'Todelliset ALV': t.todellisetAlv.join(', '),
      'Ehdotetut ALV': t.ehdotetutAlv.join(', '),
      'Tilit osumat': `${t.osumatLkm}/${t.todellisetTilit.length}`,
      'Suunta osumat': `${t.suuntaOsumatLkm}/${t.todellisetTilit.length}`,
      'Summa osumat': `${t.summaOsumatLkm}/${t.todellisetTilit.length}`,
      'ALV osumat': `${t.alvOsumatLkm}/${t.todellisetTilit.length}`,
      'Kaikki oikein': this.kaikkiOikein(t) ? 'kyllä' : 'ei',
      Virhe: t.ehdotusVirhe ?? '',
      Confidence: t.confidenceLevel ?? '',
      Perustelu: t.perustelu ?? '',
      'Input tokenit': t.inputTokenit,
      'Output tokenit': t.outputTokenit,
      Ajettu: t.ajettu,
    }))
    const ws = xlsx.utils.json_to_sheet(rivit)
    const wb = xlsx.utils.book_new()
    xlsx.utils.book_append_sheet(wb, ws, 'Tulokset')
    const malli = ajo?.malli ?? this.tuloksetTestiajoAvain
    const aikaleima = this._dateService.muotoile(new Date(), 'yyyyMMdd-HHmmss')
    xlsx.writeFile(wb, `llm-testaus-${this.kategoria}-${malli}-${aikaleima}.xlsx`)
  }
}
